import { DatePipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { Career } from '../../../../api/app-types/career.app';
import { ClientName } from '../../../../api/app-types/client.app';
import { SocialSecurityStatusActivity } from '../../../../api/app-types/social-security-status.app';
import { CarouselElement, NavCarousel } from '../../../../core/components/nav-carousel/nav-carousel';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { CareerDeleteDialog } from './career-delete-dialog';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';

@Component({
	selector: 'lvr-client-career-detail',
	templateUrl: './career-detail.html',
	styleUrl: './career-detail.scss',
	imports: [TranslocoPipe, NavCarousel, DatePipe, LvrHeading],
})
export class CareerDetail {
	private readonly router = inject(Router);
	private readonly activeRoute = inject(ActivatedRoute);
	private readonly data = toSignal(this.activeRoute.data);
	private readonly dialog = inject(MatDialog);
	private readonly session = inject(SessionManagerService);

	private readonly careerPage = computed(() => this.data()?.['careerPage'] as PaginatedResponse<Career>);
	private readonly clientId = computed(() => this.data()?.['clientId'] as number);
	public readonly career = computed(
		() => this.careerPage().entities.find((c) => c.id === Number(this.activeRoute.snapshot.paramMap.get('careerId')))!,
	);
	public readonly clientName = computed(() => this.data()?.['clientName'] as ClientName);
	public readonly carouselElements = computed(() =>
		this.careerPage().entities.map((d) => ({
			id: d.id.toString(),
			label: `${d.socialSecurityStatus.remark ?? d.id}`,
		})),
	);
	public readonly currentIndex = computed(() => {
		const id = this.career().id.toString();
		return this.carouselElements().findIndex((e) => e.id === id);
	});
	public readonly backLink = computed(() =>
		this.router.createUrlTree(['../'], { relativeTo: this.activeRoute }).toString(),
	);

	public readonly titleTranslationKey = computed(() => {
		const activity = this.career().socialSecurityStatus.statusActivity;
		if (activity !== SocialSecurityStatusActivity.S && activity !== SocialSecurityStatusActivity.N) {
			return `client.career.status.work`;
		}
		if (activity === SocialSecurityStatusActivity.S) {
			return `client.career.status.school`;
		}
		return `client.career.status.default`;
	});

	public readonly isSchoolStatus = computed(
		() => this.career().socialSecurityStatus.statusActivity === SocialSecurityStatusActivity.S,
	);

	public readonly cityDistrictTranslationKey = computed(() => {
		const activity = this.career().socialSecurityStatus.statusActivity;
		if (activity !== SocialSecurityStatusActivity.S && activity !== SocialSecurityStatusActivity.N) {
			return `base.field.workplace_district`;
		}
		if (activity === SocialSecurityStatusActivity.S) {
			return `base.field.school_district`;
		}
		return `base.field.city_district`;
	});
	public readonly getInstitutionInformedTranslationKey = computed(() => {
		const confirmed = this.career().workplacesJob?.institutionInformed;
		if (!confirmed) return '-';
		return confirmed ? 'client.career.informed' : 'client.career.nicht_informed';
	});
	public readonly isFrozen = computed(() => this.career().isFrozen);
	public readonly canEditCareers = hasPermission(this.session.permissions, 'Careers.Edit');
	public readonly canDeleteCareers = hasPermission(this.session.permissions, 'Careers.Delete');

	/**
	 * Handles the navigation from the nav carousel.
	 * Updates the route based on the selected element.
	 */
	public onNavigate(event: { index: number; element: CarouselElement }) {
		this.router.navigate(['../', event.element.id], { relativeTo: this.activeRoute });
	}

	public async openDeleteDialog() {
		await lastValueFrom(
			this.dialog
				.open(CareerDeleteDialog, {
					data: {
						careerId: this.career().id,
						clientId: this.clientId(),
						onDeleted: async () => {
							await this.router.navigate(['../'], { relativeTo: this.activeRoute });
						},
					},
				})
				.afterClosed(),
		);
	}

	/**
	 * Navigates to 'edit' view.
	 */
	public openEdit() {
		const admissionId = this.career()?.participationStatus?.id;
		return this.router.navigate(['edit'], {
			relativeTo: this.activeRoute,
			queryParams: { admissionId },
		});
	}
}
